import React from 'react';
import DateInputWithPicker from '@sabre/spark-react-core/date-selection/date-input-with-picker';
import SelectInput from '@sabre/spark-react-core/select-input';
import TextInput from '@sabre/spark-react-core/text-input';
import { func, string, bool, object, number, array } from 'prop-types';
import { displayDateTextMethod, formatDate } from '../../utils/common-util.js';
import { OPERATOR_OPTIONS, NO_MAX_LIMIT_FIELDS } from '../../utils/constants';
import { DateUtil } from '../../utils/date-util.js';
import { toastWarn } from '../../utils/toasts';
import { searchCriteriaFieldsValidation } from '../../utils/validations.js';

export default function ComparisonInput({
   onChange,
   name,
   disabled,
   value,
   dataType = 'text',
   className,
   validationName = '',
   placeHolder = '',
   characterLimit,
   inputType,
   field = '',
   ...props
}) {
   const options = OPERATOR_OPTIONS;
   const defaultOption = options[0]?.value;

   const handleOnChange = (value, operator) => {
      onChange({
         field: field,
         value: value || '',
         operator: operator || defaultOption,
      });
   };

   const handleDatePickerChange = (event) => {
      if (!event) return;

      if (event?.target?.name === `${name}-drop-down`) {
         handleOnChange(value?.value, event.target.value);
      } else {
         const formattedDate = formatDate(event);

         handleOnChange(formattedDate, value?.operator);
      }
   };

   const handleCharacterInputChange = (event) => {
      const multipleInputs = event.target.value.split(',');
      let isLimitOverFlow = false;
      let isInValidCharacter = false;
      multipleInputs.map((singleInput) => {
         const isWithinCharacterLimit =
            characterLimit && singleInput.length <= characterLimit;
         const isValidCharacter =
            searchCriteriaFieldsValidation[validationName]?.test(singleInput);
         if (
            singleInput.length > 0 &&
            !isValidCharacter &&
            !event.target.name.includes('drop-down')
         ) {
            isInValidCharacter = true;
            return;
         }
         if (event.target.name === `${name}-drop-down`) {
            isLimitOverFlow = false;
         } else {
            if (
               !isWithinCharacterLimit &&
               !NO_MAX_LIMIT_FIELDS.includes(name)
            ) {
               isLimitOverFlow = true;
            }
         }
      });
      if (event.target.name === `${name}-drop-down`) {
         handleOnChange(value?.value, event.target.value);
      } else {
         if (isLimitOverFlow)
            toastWarn(
               `Maximum ${characterLimit} characters allowed in ${name} field!`
            );
         else if (isInValidCharacter) {
            return;
         } else {
            const upperCaseValue = event.target.value.toUpperCase();
            handleOnChange(upperCaseValue, value?.operator);
         }
      }
   };

   const handleChange = (event) => {
      if (inputType === 'datePicker') {
         handleDatePickerChange(event);
      } else {
         handleCharacterInputChange(event);
      }
   };

   const handleFocus = (event) => event.target.select();

   return (
      <div className={`${className} flex`}>
         <SelectInput
            name={`${name}-drop-down`}
            data-testid={`${name}-drop-down`}
            options={options}
            value={value?.operator || defaultOption}
            disabled={disabled}
            className='select-input'
            tabIndex='-1'
            onChange={handleChange}
            label={`${name}-drop-down`}
            aria-label={`${name}-drop-down`}
         />
         <div className='input-width'>
            {inputType === 'datePicker' ? (
               <DateInputWithPicker
                  className='date-picker'
                  data-testid={`${name}-drop-down`}
                  value={new Date(value?.value || '')}
                  showDateAsText
                  separator='/'
                  displayDateTextMethod={displayDateTextMethod}
                  format={DateUtil.DATE_FORMATS.DD_MM_YYYY}
                  advanceByMonthAndYear
                  showTodayButton
                  title={`${name}-drop-down-date`}
                  onChange={handleChange}
               />
            ) : (
               <TextInput
                  name={name}
                  data-testid={`${name}-input`}
                  value={value?.value || ''}
                  disabled={disabled}
                  className={'text-input'}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  aria-label={`${name}-input`}
                  autoComplete='off'
                  type={dataType}
                  placeholder={placeHolder}
                  {...props}
               />
            )}
         </div>
      </div>
   );
}

ComparisonInput.propTypes = {
   onChange: func,
   name: string,
   disabled: bool,
   value: object,
   dataType: string,
   inputType: string,
   className: string,
   validationName: string,
   placeHolder: string,
   characterLimit: number,
   sectionDetails: array,
   field: string,
};
